/**
 * 英語口試練習畫面 — 把口試卷三大部分(朗讀 / 家庭介紹 / 問答)做成可練習的關卡。
 *
 * 每題的核心循環(boss 2026-06-09「能出成口試題目 + 也要收音檢查」):
 *   🔊 示範  → 聽 TTS 念目標句(雲端英文嗓)
 *   🔴 錄音  → 同時「收音」(MediaRecorder)+「發音檢查」(語音辨識),一鍵兩用
 *   ⏹ 停止  → 出現回放音檔 + 自動評分(念到 / 漏掉哪些字 + 等第)
 *
 * 降級:瀏覽器不支援語音辨識 → 只保留錄音回放自我檢查;不支援錄音 → 只保留發音
 * 檢查(辨識);兩者都缺 → 仍可示範 + 看題練習。
 *
 * 視覺 = 學術風格設計系統(pageShell / academicButton + --ef-* token)。專業介面
 * 鐵則:無 UI 音效 / 無彩光 / 無 mascot。學習/遊戲分離:純學習側,不碰戰鬥數值。
 */

import { speak, stopSpeaking } from '../../shared/tts-client';
import { pageShell, academicButton } from '../ui/components';
import type { OralExam, OralItem, OralPart } from './oral-exam-data';
import { demoTextFor, oralAudioUrl } from './oral-exam-data';
import { mountShadowing, type ShadowingPanelHandle } from './shadowing';
import { mountPictureTalk, type PictureTalkPanelHandle } from './picture-talk';
import {
  isRecordingSupported,
  isSpeechCheckSupported,
  ratePronunciation,
  recognizeSpeech,
  scorePronunciation,
  startRecording,
  type PronScore,
  type RecognitionHandle,
  type RecorderHandle,
} from './speech-check';

/** 一題的目標答案(可多個,例:問答的 Yes / No 兩種答法)。 */
function targetsFor(item: OralItem): string[] {
  // 問答題的 text 形如 "Yes, he can. / No, he can't.",拆成兩種可接受答法。
  if (item.text.includes('/')) {
    return item.text.split('/').map((s) => s.trim()).filter((s) => s.length > 0);
  }
  return [item.text];
}

/** 對多個可接受目標各評一次,取涵蓋率最高者。 */
function scoreBest(targets: string[], transcript: string): PronScore {
  let best: PronScore | null = null;
  for (const t of targets) {
    const s = scorePronunciation(t, transcript);
    if (!best || s.coverage > best.coverage) best = s;
  }
  return best ?? { coverage: 0, matched: [], missed: [], transcriptWords: [] };
}

/**
 * 渲染英語口試練習。`onDone` 在離開時呼叫。
 */
export function runOralExam(container: HTMLElement, exam: OralExam, onDone: () => void): void {
  container.innerHTML = '';

  const speechOk = isSpeechCheckSupported();
  const recordOk = isRecordingSupported();

  // ── 進行中錄音 / 辨識的可清理控制把手(換題 / 離開時收尾)──
  let recorder: RecorderHandle | null = null;
  let recognition: RecognitionHandle | null = null;
  let recTimer: ReturnType<typeof setInterval> | null = null;
  let lastUrl: string | null = null;
  let demoAudio: HTMLAudioElement | null = null;
  // 「句型跟讀」分頁(P3a):自含面板,切走 / 離開時 destroy 收尾。
  let shadowingActive = false;
  let shadowingPanel: ShadowingPanelHandle | null = null;
  // 「看圖開口說」分頁(P3b):同上,自含面板。
  let pictureTalkActive = false;
  let pictureTalkPanel: PictureTalkPanelHandle | null = null;

  /**
   * 示範:先播離線預產的靜態 mp3(秒播、可離線),抓不到(404 / 還沒產生 / 不能播)
   * 才退回即時雲端 TTS。讓「示範」不再每次等網路。
   */
  const playDemo = (item: OralItem, part: OralPart): void => {
    stopSpeaking();
    if (demoAudio) { try { demoAudio.pause(); } catch { /* noop */ } demoAudio = null; }
    // error 事件與 play() rejection 可能都觸發,用 once 旗標避免播兩次 fallback。
    let fellBack = false;
    const fallback = (): void => {
      if (fellBack) return;
      fellBack = true;
      void speak(demoTextFor(part, item), { lang: 'en', voice: 'cloud' });
    };
    let audio: HTMLAudioElement;
    try {
      audio = new Audio(oralAudioUrl(item));
    } catch { fallback(); return; }
    demoAudio = audio;
    audio.addEventListener('error', fallback, { once: true });
    const p = audio.play() as unknown as Promise<void> | undefined;
    if (p && typeof p.then === 'function') p.catch(fallback);
  };

  const stopAll = (): void => {
    if (shadowingPanel) { try { shadowingPanel.destroy(); } catch { /* noop */ } shadowingPanel = null; }
    if (pictureTalkPanel) { try { pictureTalkPanel.destroy(); } catch { /* noop */ } pictureTalkPanel = null; }
    stopSpeaking();
    if (demoAudio) { try { demoAudio.pause(); } catch { /* noop */ } demoAudio = null; }
    if (recTimer) { clearInterval(recTimer); recTimer = null; }
    try { recognition?.stop(); } catch { /* noop */ }
    recognition = null;
    // recorder.stop() 回傳 Promise;這裡盡力停掉,不等結果。
    if (recorder) { try { void recorder.stop(); } catch { /* noop */ } recorder = null; }
    if (lastUrl) { try { URL.revokeObjectURL(lastUrl); } catch { /* noop */ } lastUrl = null; }
  };

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: '看題念英文 → 錄音收音 → 自動檢查發音。先按「示範」聽老師念。',
    // 口試 runner:統一 ‹ 返回 / 左緣滑動先確認(避免誤離開正在進行的口試),返回時停錄音。
    onBack: () => {
      stopAll();
      onDone();
    },
    confirmOnBack: '離開後本次口試練習不會保留,確定?',
  });

  // ── 部分切換列(Part 1 / 2 / 3)──
  const tabs = document.createElement('div');
  tabs.className = 'oral-tabs';
  tabs.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px;';
  shell.body.appendChild(tabs);

  // ── 環境提示(降級說明)──
  if (!speechOk || !recordOk) {
    const note = document.createElement('div');
    note.className = 'oral-env-note';
    note.style.cssText =
      'font-size:13px;color:var(--ef-sub);background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
      'border-radius:var(--ef-radius);padding:10px 12px;margin-bottom:12px;line-height:1.6;';
    const msgs: string[] = [];
    if (!recordOk) msgs.push('此瀏覽器不支援錄音回放。');
    if (!speechOk) msgs.push('此瀏覽器不支援自動發音檢查(建議用電腦版 Chrome / Edge)。');
    note.textContent = '提示:' + msgs.join(' ') + ' 仍可用「示範」聽發音、看題練習。';
    shell.body.appendChild(note);
  }

  const stage = document.createElement('div');
  stage.className = 'oral-stage';
  shell.body.appendChild(stage);

  let partIndex = 0;
  let itemIndex = 0;

  const renderTabs = (): void => {
    tabs.innerHTML = '';
    exam.parts.forEach((part, i) => {
      const m = part.title.match(/^Part\s*\S+/); // 只取 "Part 1/2/3" 當分頁標籤
      const tabLabel = m ? m[0] : part.title;
      const btn = academicButton({
        label: tabLabel,
        variant: !shadowingActive && !pictureTalkActive && i === partIndex ? 'primary' : 'secondary',
        onClick: () => {
          if (!shadowingActive && !pictureTalkActive && i === partIndex) return;
          stopAll();
          shadowingActive = false;
          pictureTalkActive = false;
          partIndex = i;
          itemIndex = 0;
          render();
        },
      });
      btn.classList.add('oral-tab');
      tabs.appendChild(btn);
    });
    // 句型跟讀(P3a):課本句型逐句「聽示範 → 跟讀 → 逐字評分」。
    const shadowBtn = academicButton({
      label: '句型跟讀',
      variant: shadowingActive ? 'primary' : 'secondary',
      onClick: () => {
        if (shadowingActive) return;
        stopAll();
        shadowingActive = true;
        pictureTalkActive = false;
        render();
      },
    });
    shadowBtn.classList.add('oral-tab', 'oral-tab-shadowing');
    tabs.appendChild(shadowBtn);
    // 看圖開口說(P3b):看場景圖自由說英文 → 關鍵字覆蓋率評分。
    const pictureBtn = academicButton({
      label: '看圖開口說',
      variant: pictureTalkActive ? 'primary' : 'secondary',
      onClick: () => {
        if (pictureTalkActive) return;
        stopAll();
        pictureTalkActive = true;
        shadowingActive = false;
        render();
      },
    });
    pictureBtn.classList.add('oral-tab', 'oral-tab-picture-talk');
    tabs.appendChild(pictureBtn);
  };

  /** 圖卡參考區(Part 2 職業 / Part 3 動作・動物)。 */
  const buildVocabGrid = (part: OralPart): HTMLElement | null => {
    if (!part.vocab || part.vocab.length === 0) return null;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'margin-top:14px;';
    const cap = document.createElement('div');
    cap.textContent = '圖卡參考';
    cap.style.cssText = 'font-size:13px;color:var(--ef-sub);margin-bottom:8px;';
    wrap.appendChild(cap);
    const grid = document.createElement('div');
    grid.style.cssText =
      'display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:8px;';
    for (const v of part.vocab) {
      const cell = document.createElement('div');
      cell.style.cssText =
        'display:flex;flex-direction:column;align-items:center;gap:4px;padding:10px 6px;' +
        'background:var(--ef-paper-bright);border:1px solid var(--ef-line);border-radius:var(--ef-radius);';
      cell.innerHTML =
        `<span aria-hidden="true" style="font-size:26px;line-height:1;">${v.emoji}</span>` +
        `<span style="font-size:14px;color:var(--ef-ink);">${v.word}</span>`;
      grid.appendChild(cell);
    }
    wrap.appendChild(grid);
    return wrap;
  };

  const render = (): void => {
    renderTabs();
    stage.innerHTML = '';
    if (shadowingActive) {
      shadowingPanel = mountShadowing(stage);
      return;
    }
    if (pictureTalkActive) {
      pictureTalkPanel = mountPictureTalk(stage);
      return;
    }
    const part = exam.parts[partIndex];
    if (!part) return;
    const items = part.items;
    if (itemIndex >= items.length) itemIndex = items.length - 1;
    if (itemIndex < 0) itemIndex = 0;
    const item = items[itemIndex];

    // 作答說明
    const instr = document.createElement('p');
    instr.textContent = part.instructionZh + (part.seconds ? `(建議 ${part.seconds} 秒)` : '');
    instr.style.cssText = 'font-size:15px;color:var(--ef-ink-soft);line-height:1.7;margin:0 0 14px;';
    stage.appendChild(instr);

    // 題號(同部分多題時)
    if (items.length > 1) {
      const prog = document.createElement('div');
      prog.textContent = `第 ${itemIndex + 1} / ${items.length} 題`;
      prog.style.cssText =
        'font-family:var(--ef-serif);font-weight:700;font-size:16px;color:var(--ef-ink);margin-bottom:10px;';
      stage.appendChild(prog);
    }

    // 題目卡(朗讀段落 / 問答的提問句 / 家庭介紹範本)
    const card = document.createElement('div');
    card.style.cssText =
      'padding:18px 20px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
      'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';

    if (part.kind === 'qa' && item) {
      const q = document.createElement('div');
      q.textContent = item.question ?? '';
      q.style.cssText = 'font-family:var(--ef-serif);font-size:22px;color:var(--ef-ink);line-height:1.5;';
      const a = document.createElement('div');
      a.textContent = '答法:' + item.text;
      a.style.cssText = 'margin-top:10px;font-size:16px;color:var(--ef-ink-soft);';
      card.append(q, a);
    } else if (part.kind === 'introduce' && part.template) {
      const tmpl = document.createElement('div');
      tmpl.style.cssText = 'font-size:17px;color:var(--ef-ink);line-height:2;';
      tmpl.innerHTML = part.template
        .map((line) => line.replace(/____/g, '<u style="color:var(--ef-green);">____</u>'))
        .join('<br>');
      card.appendChild(tmpl);
      const sampleCap = document.createElement('div');
      sampleCap.textContent = '示範(可照念,再換成你的家人):';
      sampleCap.style.cssText = 'margin-top:14px;font-size:13px;color:var(--ef-sub);';
      const sample = document.createElement('div');
      sample.textContent = item?.text ?? '';
      sample.style.cssText =
        'margin-top:6px;font-size:15px;color:var(--ef-ink-soft);line-height:1.7;font-style:italic;';
      card.append(sampleCap, sample);
    } else if (item) {
      const t = document.createElement('div');
      t.textContent = item.text;
      t.style.cssText = 'font-family:var(--ef-serif);font-size:22px;color:var(--ef-ink);line-height:1.6;';
      card.appendChild(t);
    }

    if (item?.hintZh) {
      const hint = document.createElement('div');
      hint.textContent = '💡 ' + item.hintZh;
      hint.style.cssText = 'margin-top:12px;font-size:14px;color:var(--ef-sub);line-height:1.6;';
      card.appendChild(hint);
    }
    stage.appendChild(card);

    // ── 操作列 ──
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;margin-top:16px;align-items:center;';
    stage.appendChild(controls);

    // 示範:先播離線靜態 mp3,抓不到才即時 TTS(問答念「問題 + 兩種答法」)。
    const demoBtn = academicButton({
      label: '🔊 示範',
      variant: 'secondary',
      onClick: () => {
        if (!item) return;
        playDemo(item, part);
      },
    });
    demoBtn.classList.add('oral-demo');
    controls.appendChild(demoBtn);

    // 結果區(回放 + 評分)
    const result = document.createElement('div');
    result.className = 'oral-result';
    result.style.cssText = 'margin-top:14px;display:flex;flex-direction:column;gap:10px;';
    // 先建好,錄音 / 檢查回來時填入。

    // 錄音(收音 + 同時發音檢查):一鍵兩用。
    let recording = false;
    const recBtn = academicButton({
      label: recordOk ? '🔴 錄音' : '🎤 發音檢查',
      variant: 'primary',
      onClick: () => {
        if (recording) { void finishRecording(); return; }
        void beginRecording();
      },
    });
    recBtn.classList.add('oral-record');
    controls.appendChild(recBtn);

    const timerEl = document.createElement('span');
    timerEl.className = 'oral-timer';
    timerEl.style.cssText = 'font-size:14px;color:var(--ef-sub);min-width:64px;';
    controls.appendChild(timerEl);

    let liveTranscriptEl: HTMLElement | null = null;
    let recognitionPromise: Promise<string> | null = null;

    const beginRecording = async (): Promise<void> => {
      recording = true;
      recBtn.textContent = '⏹ 停止';
      result.innerHTML = '';
      let secs = 0;
      timerEl.textContent = '0 秒';
      recTimer = setInterval(() => { secs += 1; timerEl.textContent = `${secs} 秒`; }, 1000);

      // 即時逐字稿(支援辨識時)
      if (speechOk) {
        liveTranscriptEl = document.createElement('div');
        liveTranscriptEl.style.cssText =
          'font-size:14px;color:var(--ef-ink-soft);min-height:20px;font-style:italic;';
        liveTranscriptEl.textContent = '🎤 聽你說…';
        result.appendChild(liveTranscriptEl);
        if (!result.isConnected) stage.appendChild(result);
        const { promise, handle } = recognizeSpeech('en-US', (t) => {
          if (liveTranscriptEl) liveTranscriptEl.textContent = t ? `「${t}」` : '🎤 聽你說…';
        });
        recognition = handle;
        recognitionPromise = promise.catch(() => '');
      }

      // 錄音(支援時)
      if (recordOk) {
        try {
          recorder = await startRecording();
        } catch (e) {
          console.warn('[oral] 錄音啟動失敗(改用發音檢查):', e);
          recorder = null;
        }
      }
    };

    const finishRecording = async (): Promise<void> => {
      recording = false;
      recBtn.textContent = recordOk ? '🔴 重新錄音' : '🎤 再檢查一次';
      if (recTimer) { clearInterval(recTimer); recTimer = null; }
      timerEl.textContent = '';

      // 停辨識,拿逐字稿
      try { recognition?.stop(); } catch { /* noop */ }
      recognition = null;
      const transcript = recognitionPromise ? await recognitionPromise : '';
      recognitionPromise = null;

      // 停錄音,拿音檔
      let audioUrl: string | null = null;
      if (recorder) {
        try {
          const { url } = await recorder.stop();
          audioUrl = url;
          if (lastUrl) { try { URL.revokeObjectURL(lastUrl); } catch { /* noop */ } }
          lastUrl = url;
        } catch (e) {
          console.warn('[oral] 錄音停止失敗:', e);
        }
        recorder = null;
      }

      renderResult(transcript, audioUrl);
    };

    const renderResult = (transcript: string, audioUrl: string | null): void => {
      result.innerHTML = '';
      if (!result.isConnected) stage.appendChild(result);

      // 回放(收音結果)
      if (audioUrl) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:10px;';
        const label = document.createElement('span');
        label.textContent = '🎧 你的錄音:';
        label.style.cssText = 'font-size:14px;color:var(--ef-ink-soft);';
        const audio = document.createElement('audio');
        audio.controls = true;
        audio.src = audioUrl;
        audio.className = 'oral-playback';
        row.append(label, audio);
        result.appendChild(row);
      }

      // 自動發音評分
      if (speechOk && item) {
        const score = scoreBest(targetsFor(item), transcript);
        const rating = ratePronunciation(score.coverage);
        const toneColor =
          rating.tone === 'good' ? 'var(--ef-green)'
            : rating.tone === 'ok' ? 'var(--ef-ink-soft)'
              : 'var(--ef-danger, #b4434a)';

        const badge = document.createElement('div');
        badge.className = 'oral-score';
        badge.style.cssText =
          `display:inline-flex;align-items:center;gap:8px;font-family:var(--ef-serif);font-weight:700;` +
          `font-size:18px;color:${toneColor};`;
        badge.textContent = `發音 ${rating.percent}% · ${rating.label}`;
        result.appendChild(badge);

        // 念到 / 待加強的字
        if (transcript.trim().length > 0) {
          const detail = document.createElement('div');
          detail.style.cssText = 'font-size:14px;color:var(--ef-ink-soft);line-height:1.8;';
          const heard = document.createElement('div');
          heard.textContent = `辨識到:${transcript}`;
          detail.appendChild(heard);
          if (score.missed.length > 0) {
            const miss = document.createElement('div');
            miss.innerHTML = `待加強:<span style="color:${'var(--ef-danger,#b4434a)'};">${score.missed.join('、')}</span>`;
            detail.appendChild(miss);
          }
          result.appendChild(detail);
        } else {
          const noSpeech = document.createElement('div');
          noSpeech.textContent = '沒聽到聲音,再大聲念一次試試。';
          noSpeech.style.cssText = 'font-size:14px;color:var(--ef-sub);';
          result.appendChild(noSpeech);
        }
      }
    };

    stage.appendChild(result);

    // 圖卡參考
    const vocab = buildVocabGrid(part);
    if (vocab) stage.appendChild(vocab);

    // ── 上下題 ──
    if (items.length > 1) {
      const nav = document.createElement('div');
      nav.style.cssText = 'display:flex;gap:10px;margin-top:18px;';
      const prev = academicButton({
        label: '← 上一題',
        variant: 'secondary',
        onClick: () => { if (itemIndex > 0) { stopAll(); itemIndex -= 1; render(); } },
      });
      prev.classList.add('oral-prev');
      const next = academicButton({
        label: '下一題 →',
        variant: 'secondary',
        onClick: () => { if (itemIndex < items.length - 1) { stopAll(); itemIndex += 1; render(); } },
      });
      next.classList.add('oral-next');
      (prev as HTMLButtonElement).disabled = itemIndex === 0;
      (next as HTMLButtonElement).disabled = itemIndex === items.length - 1;
      nav.append(prev, next);
      stage.appendChild(nav);
    }
  };

  render();
}
